import { el, addClass, on, queryAll } from '../util/dom';
import { ScheduleWindow, DayLimitOverride, DAY_SHORT_NAMES, formatScheduleWindow } from './types';
import { SCHEDULES_LIST_STYLES } from './styles';

export interface SchedulesListProps {
  schedules: ScheduleWindow[];
  dayOverrides: DayLimitOverride[];
  loading: boolean;
  onEditSchedule: (schedule: ScheduleWindow) => void;
  onDeleteSchedule: (scheduleId: string) => Promise<void>;
  onAddSchedule: () => void;
  onEditOverride: (override: DayLimitOverride) => void;
  onDeleteOverride: (overrideId: string) => Promise<void>;
  onAddOverride: () => void;
}

export function createSchedulesList(props: SchedulesListProps): HTMLElement {
  const container = el('div', { class: 'schedules-list-container' });

  // Schedules section
  const schedulesSection = createSchedulesSection(props);
  container.appendChild(schedulesSection);

  // Day overrides section
  const overridesSection = createDayOverridesSection(props);
  container.appendChild(overridesSection);

  // Inject styles
  injectSchedulesStyles();

  return container;
}

function createSchedulesSection(props: SchedulesListProps): HTMLElement {
  const section = el('div', { class: 'schedules-section' });

  // Header with add button
  const header = el('div', { class: 'schedules-header' });
  const title = el('h3', { class: 'schedules-title' }, 'Curfews');
  header.appendChild(title);

  const addBtn = el('button', { class: 'btn btn-primary btn-sm' }, '+ Add Curfew');
  on(addBtn, 'click', () => props.onAddSchedule());
  header.appendChild(addBtn);
  section.appendChild(header);

  // Loading state
  if (props.loading) {
    const loading = el('div', { class: 'schedules-loading' }, 'Loading curfews...');
    section.appendChild(loading);
    return section;
  }

  // Empty state
  if (props.schedules.length === 0) {
    const empty = el('div', { class: 'schedules-empty' },
      'No curfews set. Add one to block all restricted apps during quiet hours.');
    section.appendChild(empty);
    return section;
  }

  // List
  const list = el('div', { class: 'schedules-list' });
  props.schedules.forEach(schedule => {
    const item = createScheduleItem(schedule, props);
    list.appendChild(item);
  });
  section.appendChild(list);

  return section;
}

function createScheduleItem(schedule: ScheduleWindow, props: SchedulesListProps): HTMLElement {
  const item = el('div', { class: 'schedule-item' });

  // Display formatted schedule
  const info = el('div', { class: 'schedule-info' });
  const formatted = el('div', { class: 'schedule-formatted' }, formatScheduleWindow(schedule));
  info.appendChild(formatted);
  item.appendChild(info);

  // Actions
  const actions = el('div', { class: 'schedule-actions' });

  const editBtn = el('button', { class: 'btn btn-sm btn-secondary' }, 'Edit');
  on(editBtn, 'click', () => props.onEditSchedule(schedule));
  actions.appendChild(editBtn);

  const deleteBtn = el('button', { class: 'btn btn-sm btn-danger' }, 'Delete');
  on(deleteBtn, 'click', async () => {
    if (confirm(`Delete this curfew?`)) {
      deleteBtn.disabled = true;
      try {
        await props.onDeleteSchedule(schedule.id);
      } catch (err) {
        alert(`Failed to delete: ${(err as Error).message}`);
        deleteBtn.disabled = false;
      }
    }
  });
  actions.appendChild(deleteBtn);

  item.appendChild(actions);

  return item;
}

function createDayOverridesSection(props: SchedulesListProps): HTMLElement {
  const section = el('div', { class: 'day-overrides-section' });

  // Header with add button
  const header = el('div', { class: 'day-overrides-header' });
  const title = el('h3', { class: 'day-overrides-title' }, 'Daily Limit Overrides');
  header.appendChild(title);

  const addBtn = el('button', { class: 'btn btn-primary btn-sm' }, '+ Add Override');
  on(addBtn, 'click', () => props.onAddOverride());
  header.appendChild(addBtn);
  section.appendChild(header);

  const hint = el('div', { class: 'day-overrides-hint' },
    'Override the daily screen time limit for specific days (e.g., weekends).');
  section.appendChild(hint);

  // Empty state
  if (props.dayOverrides.length === 0) {
    const empty = el('div', { class: 'day-overrides-empty' }, 'No daily limit overrides set.');
    section.appendChild(empty);
    return section;
  }

  // List
  const list = el('div', { class: 'day-overrides-list' });
  props.dayOverrides.forEach(override => {
    const item = createOverrideItem(override, props);
    list.appendChild(item);
  });
  section.appendChild(list);

  return section;
}

function createOverrideItem(override: DayLimitOverride, props: SchedulesListProps): HTMLElement {
  const item = el('div', { class: 'override-item' });

  // Display override info
  const info = el('div', { class: 'override-info' });
  const day = el('div', { class: 'override-day' }, DAY_SHORT_NAMES[override.dayOfWeek]);
  const limit = el('div', { class: 'override-limit' }, `${override.limitMinutes} min`);
  info.appendChild(day);
  info.appendChild(limit);
  item.appendChild(info);

  // Actions
  const actions = el('div', { class: 'override-actions' });

  const editBtn = el('button', { class: 'btn btn-sm btn-secondary' }, 'Edit');
  on(editBtn, 'click', () => props.onEditOverride(override));
  actions.appendChild(editBtn);

  const deleteBtn = el('button', { class: 'btn btn-sm btn-danger' }, 'Delete');
  on(deleteBtn, 'click', async () => {
    if (confirm(`Delete override for ${DAY_SHORT_NAMES[override.dayOfWeek]}?`)) {
      deleteBtn.disabled = true;
      try {
        await props.onDeleteOverride(override.id);
      } catch (err) {
        alert(`Failed to delete: ${(err as Error).message}`);
        deleteBtn.disabled = false;
      }
    }
  });
  actions.appendChild(deleteBtn);

  item.appendChild(actions);

  return item;
}

function injectSchedulesStyles() {
  if (document.getElementById('schedules-styles')) return;

  const style = el('style');
  style.id = 'schedules-styles';
  style.textContent = SCHEDULES_LIST_STYLES;
  document.head.appendChild(style);
}
